/**
 * ClientService
 *
 * This service handles all client-related operations in the application.
 * It provides methods for retrieving, creating, and managing client data,
 * as well as retrieving associated tasks and matter counts.
 *
 * @class
 */

import HttpService from './HttpService';
import type {
  AddClientRequest,
  AddClientResponse,
  ClientApiResponse,
  GetClientCountResponse,
  GetClientTaskCountResponse,
} from '@frontend/Models/Client/ClientRequestResponseModels';
import type { Client } from '@frontend/Models/Client/ClientModels';
import {
  pathClientCount,
  pathClientGet,
  pathClients,
  pathDeleteClient,
  pathEngClients,
  pathRenameClient,
  pathTasksByClient,
  pathTasksCountByClient,
  pathUpdateClientId,
} from '@frontend/__settings__/septSoftwarePaths';
import { GetTasksByClientResponse } from '@frontend/Models/Tasks/TaskRequestResponseModels';

const http = new HttpService();

class ClientService {
  /**
   * Retrieves all clients from the API, sorted by ClientID
   *
   * @returns {Promise<Array>} Array of client objects, sorted by ClientID
   * @throws {Error} If the API request fails
   */
  static async getClients(): Promise<Client[]> {
    const response = await http.get<ClientApiResponse<Client[]>>(`/${pathClients}`);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching clients.');
    }
    return response.data.sort((a, b) => a.ClientID.localeCompare(b.ClientID));
  }

  /**
   * Creates a new client in the system.
   *
   * Supports an optional caller-supplied `ClientID`. When omitted the backend
   * auto-generates one. A duplicate `ClientID` (or matching core details)
   * surfaces as a 409 from the backend, which axios interceptors normalize
   * into a thrown Error carrying the backend `error` message — callers can
   * forward that to `toastService.error()`.
   *
   * @param {AddClientRequest} client - The client data to be created
   * @returns {Promise<boolean>} True when the client was created successfully
   * @throws {Error} When the network request fails or the backend returns
   *   `successful: false` (e.g. duplicate ClientID, validation error).
   */
  static async postClient(client: AddClientRequest): Promise<boolean> {
    const response = await http.post<ClientApiResponse<AddClientResponse>>(`/${pathClients}`, client);
    if (response.successful !== true) {
      throw new Error(response.error || 'Failed to create client.');
    }
    return response.data?.message === 'Client added successfully.';
  }

  /**
   * Retrieves the total count of clients
   *
   * @returns {Promise<number>} Total number of clients
   * @throws {Error} If the API request fails
   */
  static async getClientCount(): Promise<number> {
    const response = await http.get<ClientApiResponse<GetClientCountResponse>>(`/${pathClientCount}`);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching client count.');
    }
    return response.data.count;
  }

  /**
   * Retrieves detailed information about a specific client
   *
   * @param {string} clientId - The ID of the client to retrieve
   * @returns {Promise<Object|null>} Client details or null if not found
   * @throws {Error} If the API request fails
   */
  static async getClientById(clientId: string): Promise<Client | null> {
    try {
      const response = await http.get<ClientApiResponse<Client>>(`/${pathClientGet(clientId)}`);
      if (!response.successful || !response.data) {
        return null;
      }
      return response.data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Retrieves all tasks associated with a specific client
   *
   * @param {string} clientId - The ID of the client
   * @returns {Promise<Array|null>} Array of tasks or null if not found
   * @throws {Error} If the API request fails
   */
  static async getTasksByClient(clientId: string): Promise<GetTasksByClientResponse[] | null> {
    try {
      const response = await http.get<ClientApiResponse<GetTasksByClientResponse[]>>(`/${pathTasksByClient(clientId)}`);
      if (!response.successful || !response.data) {
        return null;
      }
      return response.data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Retrieves the count of tasks associated with a client
   *
   * @param {string} clientId - The ID of the client
   * @returns {Promise<number|null>} Number of tasks or null if not found
   * @throws {Error} If the API request fails
   */
  static async getTasksCountByClient(clientId: string): Promise<number | null> {
    try {
      const response = await http.get<ClientApiResponse<GetClientTaskCountResponse>>(`/${pathTasksCountByClient(clientId)}`);
      if (!response.successful || !response.data) {
        return null;
      }
      return response.data.count;
    } catch (error) {
      return null;
    }
  }

  /**
   * Triggers the addition of a new engineering client
   * @param client the client data to be added
   * @returns 
   */
  static async addEngClient(client: AddClientRequest) {
    const response = await http.post<ClientApiResponse<AddClientResponse>>(`/${pathEngClients}`, client);
    return response.successful === true && response.data?.message === 'Client added successfully.';
  }

  /**
   * This method updates an existing client's information.
   * @param clientId the ID of the client to update
   * @param clientData the updated client data
   * @returns 
   */
  static async updateClient(clientId: string, clientData: any): Promise<boolean> {
    const response = await http.put<ClientApiResponse<AddClientResponse>>(`/${pathUpdateClientId(clientId)}`, clientData);
    return response.successful === true && response.data?.message === 'Client updated successfully.';
  }

  /**
   * Renames a client's ID. The backend cascades the change to every table
   * referencing the client, and also updates the IDs of the client's matters
   * that embed the client ID (e.g. "322230-S0002" -> "322230-NEWID").
   *
   * @param clientId the current client ID
   * @param newClientId the new client ID
   * @returns the renamed matters mapping (old matter ID -> new matter ID)
   * @throws {HttpError} status 409 if the new ID is already taken
   */
  static async renameClient(
    clientId: string,
    newClientId: string
  ): Promise<Record<string, string>> {
    const response = await http.put<ClientApiResponse<{ message: string; newClientID: string; renamedMatters: Record<string, string> }>>(
      `/${pathRenameClient(clientId)}`,
      { new_clientid: newClientId }
    );
    if (response.successful !== true || !response.data) {
      throw new Error(response.error || 'Failed to rename client.');
    }
    return response.data.renamedMatters ?? {};
  }

  /**
   * Deletes a client by their ID.
   *
   * @param {string} clientId - The ID of the client to delete
   * @returns {Promise<boolean>} True if the client was deleted successfully
   * @throws {Error} If the API request fails
   */
  static async deleteClient(clientId: string): Promise<boolean> {
    const response = await http.delete<ClientApiResponse<{ message: string; ClientID: string }>>(`/${pathDeleteClient(clientId)}`);
    return response.successful === true && response.data?.message === 'Client deleted successfully.';
  }
}

export default ClientService;
